import axios, { CreateAxiosDefaults } from 'axios';
import axiosRetry, { exponentialDelay } from 'axios-retry';
import isArray from 'lodash/isArray';
import isPlainObject from 'lodash/isPlainObject';

import { IBaseArrayResultDto } from './IBaseResultDto';

export const REQUEST_RETRIES = 3;

axiosRetry(axios, {
  retries: REQUEST_RETRIES,
  retryDelay: exponentialDelay,
});

export interface IRequestConfig {
  data?: unknown;
  headers?: Record<string, string | number | boolean>;
  method?: string;
  params?: unknown;
  url?: string;
}

export type TInterceptorFunction = (
  response: IBaseArrayResultDto,
) => boolean | void | Promise<boolean | void>;

export interface IRequestorExtras {
  deviceIdProvider?: () => string | null | undefined;
  deviceIdHeaderName?: string;
}

export const request = (
  config: CreateAxiosDefaults = {},
  errorInterceptors: TInterceptorFunction[] = [],
  extras: IRequestorExtras = {},
) => {
  const transport = axios.create({
    baseURL: '',
    timeout: 30000,
    withCredentials: false,
    headers: {
      'Content-Type': 'application/json',
    },
    ...config,
  });

  const deviceIdProvider = extras.deviceIdProvider;
  const deviceIdHeaderName = extras.deviceIdHeaderName ?? 'X-Device-Id';

  if (typeof deviceIdProvider === 'function') {
    transport.interceptors.request.use((requestConfig) => {
      try {
        const deviceId = deviceIdProvider();
        if (typeof deviceId === 'string' && deviceId.length > 0) {
          if (!requestConfig.headers) {
            requestConfig.headers = {} as typeof requestConfig.headers;
          }
          (requestConfig.headers as Record<string, unknown>)[
            deviceIdHeaderName
          ] = deviceId;
        }
      } catch {
        // ignore provider errors — header is best-effort
      }

      return requestConfig;
    });
  }

  return async (
    request: IRequestConfig & {
      errorInterceptor?: TInterceptorFunction;
      loading: boolean;
      showError: boolean;
    },
  ) => {
    let response,
      error = '',
      success = false;
    try {
      response = await transport(request);
      if (!response) throw new Error('Server connection error');
      success = true;
    } catch (err: any) {
      response = err.response;
      error =
        response?.data?.message ??
        err.message ??
        (!response ? 'Server connection error' : 'Unknown error');

      if (isArray(error)) error = error.join(', ');
      if (isPlainObject(error)) error = Object.values(error).join(', ');
    }
    if (!response) response = {};
    if (!response.data) response.data = {};
    if (typeof response.data !== 'object' || response.data === null) {
      response.data = { data: response.data };
    }
    response.data.success = success;
    response.data.error = error;
    response.data.status = response.status;

    if (response.data.error) {
      const allErrorInterceptors = [
        ...(request.errorInterceptor ? [request.errorInterceptor] : []),
        ...errorInterceptors,
      ];
      for (let i = 0; i < allErrorInterceptors.length; i++) {
        const stopErrorIntercepting = await allErrorInterceptors[i](
          response.data,
        );
        if (stopErrorIntercepting) break;
      }
    }

    return response;
  };
};
